import React, {Fragment} from 'react'
import useEffect from 'admin/hooks/useEffect'
import usePayment from 'admin/hooks/usePayment'
import useDocument from 'admin/hooks/useDocument'
import useBoolState from 'admin/hooks/useBoolState'
import useStateObject from 'admin/hooks/useStateObject'
import InputField from 'admin/components/shared/InputField'
import DateField from 'admin/components/shared/DateField'
import Modal from 'admin/components/shared/Modal'
import Header from './shared/Header'
import ShowField from './shared/ShowField/index'
import Deletion from './Deletion'
import {useHistory, useParams} from 'react-router-dom'
import useModal from '../hooks/useModal'
import ModalLink from './shared/ModalLink'
import {formatTime} from '../lib/format'
import AccountField from './shared/AccountField'

export default () => {
  const history = useHistory()
  const {organisationID} = useParams()
  const {paymentId, accountId, popModal} = useModal()
  const {payment, errors, update, destroy} = usePayment(organisationID, accountId, paymentId)
  const [showConfirmDelete, setConfirmDelete, toggleConfirmDelete] = useBoolState()
  const [form, setForm, resetForm] = useStateObject(payment)

  const title = `Payment ${paymentId}`
  useDocument({title})

  useEffect(() => resetForm(payment), [payment], false)

  const handleUpdate = e => {
    e.preventDefault()
    update(form)
  }

  const handleClose = e => {
    e.preventDefault()
    resetForm(payment)
    popModal('payment', ['paymentId', 'accountId'])
  }

  if (!payment.id) return null

  const locked = payment.state === 'locked'
  const imported = !!payment.xeroLink || !!payment.bankImport

  return (
    <Fragment>
      <Modal onClose={handleClose} flashId='edit-account-credit'>
        {locked && (
          <section className='py-3 section has-background-dark'>
            <div className='container'>
              <span className='icon-text'>
                <span className='icon'>
                  <i className='fas fa-lock'></i>
                </span>
                <span className='is-size-7 has-text-light ml-2'>This payment is locked and cannot be edited.</span>
              </span>
            </div>
          </section>
        )}
        <Header
          title={title}
          subtitle={
            payment.xeroLink && (
              <h5 className='subtitle is-7'>
                <a href={payment.xeroLink} target='_blank'>
                  <span>Imported from Xero</span>{' '}
                  <span className='icon'>
                    <i className='fa fa-external-link'></i>
                  </span>
                </a>
              </h5>
            )
          }>
          <ModalLink modal='history' params={{itemType: 'Payment', itemId: paymentId}} className='button is-white'>
            <span className='icon is-small'>
              <i className='fa fa-history' aria-hidden='true'></i>
            </span>
            <span>View History</span>
          </ModalLink>
          {locked ? null : (
            <Fragment>
              <button className='button is-danger is-outlined' onClick={toggleConfirmDelete}>
                Delete
              </button>
              {payment.state === 'draft' ? (
                <button className='button is-primary is-outlined' onClick={e => update({state: 'reconciled'})}>
                  Reconcile
                </button>
              ) : (
                <button className='button is-info is-outlined' onClick={e => update({state: 'draft'})}>
                  Un-reconcile
                </button>
              )}
              <button className='button is-primary' onClick={handleUpdate}>
                Save
              </button>
            </Fragment>
          )}
        </Header>
        <section className='section is-narrow'>
          <div className='container'>
            <form>
              <div className='field'>
                <div className='control'>
                  <AccountField
                    label='Account'
                    value={form.account?.displayName}
                    organisationID={organisationID}
                    onSelect={account => setForm({account, accountId: account?.id})}
                    errors={errors.accountId}
                    disabled={locked || payment.state !== 'draft'}
                  />
                </div>
              </div>

              <div className='field is-grouped'>
                <div className='control is-expanded'>
                  <DateField
                    name='date'
                    label='Date received'
                    value={form.date || ''}
                    onChange={setForm}
                    errors={errors.date}
                    disabled={locked || imported}
                    required
                  />
                </div>

                <div className='control is-expanded'>
                  <InputField
                    type='number'
                    name='amount'
                    value={form.amount || ''}
                    onChange={setForm}
                    errors={errors.amount}
                    min={0}
                    autoFocus={true}
                    disabled={locked || imported}
                    required
                  />
                </div>
              </div>

              <div className='field'>
                <div className='control'>
                  <InputField
                    type='text'
                    name='description'
                    value={form.description || ''}
                    onChange={setForm}
                    errors={errors.description}
                    disabled={locked}
                    required
                  />
                </div>
              </div>
            </form>
          </div>
        </section>
        {!!payment.bankImport || !!payment.invoice ? (
          <section className='py-3 section has-background-info'>
            <div className='container is-size-7 has-text-light'>
              <ShowField
                value={payment.importDetails?.trim()}
                alwaysHorizontal
                rightAligned
                className='mb-0'
                labelClassName='has-text-light'
                label='Details'
              />
              <ShowField
                value={payment.importParticulars?.trim()}
                alwaysHorizontal
                rightAligned
                className='mb-0'
                labelClassName='has-text-light'
                label='Particulars'
              />
              <ShowField
                value={payment.importReference?.trim()}
                alwaysHorizontal
                rightAligned
                className='mb-0'
                labelClassName='has-text-light'
                label='Reference'
              />
              <ShowField
                value={payment.importCode?.trim()}
                alwaysHorizontal
                rightAligned
                className='mb-0'
                labelClassName='has-text-light'
                label='Code'
              />
              <ShowField
                value={
                  payment.bankImport &&
                  `${payment.bankImport.name}${organisationID !== payment.bankImport.organisation.id ? ` (${payment.bankImport.organisation.internalName})` : ''}`
                }
                alwaysHorizontal
                rightAligned
                className='mb-0'
                labelClassName='has-text-light'
                label='Imported with'
              />
              <ShowField
                value={formatTime(payment.reconciledAt)}
                alwaysHorizontal
                rightAligned
                className='mb-0'
                labelClassName='has-text-light'
                label='Last reconciled'
              />
              <ShowField
                value={payment.invoice?.invoiceNumber}
                href={`/orgs/${organisationID}/invoices/${payment.invoice?.id}`}
                alwaysHorizontal
                rightAligned
                className='mb-0'
                labelClassName='has-text-light'
                label='Attached to'
              />
            </div>
          </section>
        ) : null}
      </Modal>

      {showConfirmDelete && (
        <Deletion
          confirm='Are you sure you want to delete?'
          onConfirm={() => destroy({}, history.goBack)}
          onClose={toggleConfirmDelete}
        />
      )}
    </Fragment>
  )
}
